import Header from "../../../Header/Header";
import Sidebar from "../../../Sidebar/Sidebar";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlinePhotograph,
  HiOutlineHeart,
  HiOutlineChat,
  HiOutlineTrash,
  HiOutlinePencil,
  HiOutlineEye,
  HiOutlinePlus,
  HiOutlineX,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineOfficeBuilding,
  HiOutlineCalendar,
  HiOutlineUpload,
  HiOutlineExclamation,
  HiOutlineCheckCircle
} from "react-icons/hi";
import Cookies from "js-cookie";
import axios from "axios";
import { toast } from "react-hot-toast";

const Posts = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [formData, setFormData] = useState({
    caption: "",
    image: null,
    imagePreview: ""
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  const postsPerPage = 9;
  const authToken = Cookies.get("authToken");
  const adminId = localStorage.getItem("adminId");

  // Fetch posts on component mount and page change
  useEffect(() => {
    fetchPosts();
  }, [currentPage]);

  // Handle escape key for modals
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showCreateModal) setShowCreateModal(false);
        if (showEditModal) setShowEditModal(false);
        if (showViewModal) setShowViewModal(false);
        if (showDeleteModal) setShowDeleteModal(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showCreateModal, showEditModal, showViewModal, showDeleteModal]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllAdminPosts`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data?.success) {
        const postsData = response.data.message || [];
        setPosts(postsData);
        setTotalPages(Math.ceil(postsData.length / postsPerPage));
      } else {
        toast.error("Failed to fetch posts");
        setPosts([]);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Error fetching posts");
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const paginatedPosts = Array.isArray(posts) 
    ? posts.slice(
        (currentPage - 1) * postsPerPage,
        currentPage * postsPerPage
      )
    : [];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    }
    
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const validateForm = (isEdit = false) => {
    const errors = {};
    
    if (!formData.caption.trim()) {
      errors.caption = "Caption is required";
    } else if (formData.caption.length < 10) {
      errors.caption = "Caption must be at least 10 characters";
    } else if (formData.caption.length > 500) {
      errors.caption = "Caption must be less than 500 characters";
    }

    if (!isEdit && !formData.image) {
      errors.image = "Image is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        toast.error("Please select an image file (JPEG, PNG, GIF)");
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      // Simulate upload progress
      setImageUploadProgress(0);
      const interval = setInterval(() => {
        setImageUploadProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 10;
        });
      }, 50);

      setFormData({
        ...formData,
        image: file,
        imagePreview: URL.createObjectURL(file)
      });
      
      // Clear error for image
      setFormErrors(prev => ({ ...prev, image: null }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const resetForm = () => {
    setFormData({
      caption: "",
      image: null,
      imagePreview: ""
    });
    setFormErrors({});
    setImageUploadProgress(0);
  };

  const handleCreatePost = async () => {
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    if (!adminId) {
      toast.error("Admin ID not found. Please login again.");
      return;
    }

    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("caption", formData.caption);
      formDataToSend.append("image", formData.image);
      formDataToSend.append("createdBy", adminId);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/createPost`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.success) {
        toast.success("Post created successfully", {
          icon: '✅',
          duration: 3000
        });
        setShowCreateModal(false);
        resetForm();
        fetchPosts();
      } else {
        toast.error(response.data?.message || "Failed to create post");
      }
    } catch (error) {
      console.error("Error creating post:", error);
      toast.error(error.response?.data?.message || "Error creating post");
    } finally {
      setSubmitting(false);
      setImageUploadProgress(0);
    }
  };

  const handleEditPost = async () => {
    if (!selectedPost) return;

    if (!validateForm(true)) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("caption", formData.caption);
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/updatePost/${selectedPost._id}`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.success) {
        toast.success("Post updated successfully", {
          icon: '✅',
          duration: 3000
        });
        setShowEditModal(false);
        resetForm();
        setSelectedPost(null);
        fetchPosts();
      } else {
        toast.error(response.data?.message || "Failed to update post");
      }
    } catch (error) {
      console.error("Error updating post:", error);
      toast.error(error.response?.data?.message || "Error updating post");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePost = async () => {
    if (!selectedPost) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/deletePost/${selectedPost._id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data?.success) {
        toast.success("Post deleted successfully", {
          icon: '🗑️',
          duration: 3000
        });
        setShowDeleteModal(false);
        setSelectedPost(null);
        fetchPosts();
      } else {
        toast.error(response.data?.message || "Failed to delete post");
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error(error.response?.data?.message || "Error deleting post");
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (post) => {
    setSelectedPost(post);
    setFormData({
      caption: post.caption || "",
      image: null,
      imagePreview: post.image || ""
    });
    setShowEditModal(true);
  };

  const openViewModal = (post) => {
    setSelectedPost(post);
    setShowViewModal(true);
  };

  const openDeleteModal = (post) => {
    setSelectedPost(post);
    setShowDeleteModal(true);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { type: "spring", damping: 25, stiffness: 300 }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9,
      transition: { duration: 0.2 }
    }
  };

  const SkeletonLoader = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
      {[...Array(6)].map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="h-48 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse" />
          <div className="p-4 space-y-3">
            <div className="flex items-center space-x-2">
              <div className="w-5 h-5 bg-gray-200 rounded-full animate-pulse" />
              <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
            </div>
            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
            <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2" />
            <div className="flex justify-between">
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200"
    >
      <div className="w-24 h-24 mx-auto bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4 border border-gray-200">
        <HiOutlinePhotograph className="w-12 h-12 text-gray-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">No Posts Yet</h3>
      <p className="text-gray-500 mb-6 max-w-md mx-auto">
        Create your first post to engage with your audience. Share updates, announcements, and more.
      </p>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowCreateModal(true)}
        className="px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition-all duration-300 inline-flex items-center gap-2 shadow-lg"
      >
        <HiOutlinePlus className="w-5 h-5" />
        <span>Create Your First Post</span>
      </motion.button>
    </motion.div>
  );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 w-full">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} activePage="/posts" />

      <div className="flex-1 w-0">
        <Header toggleSidebar={toggleSidebar} />

        <main className="p-4 sm:p-6 lg:p-8 overflow-auto">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8"
          >
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Posts Management</h1>
              <p className="text-sm text-gray-500 mt-1">
                {posts.length} post{posts.length !== 1 ? 's' : ''} • Page {currentPage} of {totalPages}
              </p>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateModal(true)}
              className="w-full sm:w-auto bg-gray-800 text-white px-6 py-3 rounded-xl shadow-lg hover:bg-gray-900 transition-all duration-300 flex items-center justify-center gap-2 text-sm font-medium"
            >
              <HiOutlinePlus className="w-5 h-5" />
              <span>Create New Post</span>
            </motion.button>
          </motion.div>

          {/* Posts Grid */}
          {loading ? (
            <SkeletonLoader />
          ) : !posts || posts.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
              >
                {paginatedPosts.map((post) => {
                  const collegeName = post.createdBy?.collegeName || 
                                      post.adminProfile?.collegeName || 
                                      "Unknown College";
                  
                  return (
                    <motion.div
                      key={post._id}
                      variants={itemVariants}
                      whileHover={{ y: -4 }}
                      className="bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden group"
                    >
                      {/* Image with overlay */}
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.caption}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        
                        {/* Quick view button on hover */}
                        <motion.button
                          initial={{ opacity: 0, y: 20 }}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          animate={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          onClick={() => openViewModal(post)}
                          className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                          <HiOutlineEye className="w-4 h-4 text-gray-700" />
                        </motion.button>
                      </div>

                      {/* Content */}
                      <div className="p-4">
                        {/* College Name and Date */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-500" />
                            <span className="text-xs font-medium text-gray-700 truncate max-w-[120px]">
                              {collegeName}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <HiOutlineCalendar className="w-4 h-4 text-gray-500" />
                            <span className="text-xs text-gray-500">
                              {formatDate(post.createdAt)}
                            </span>
                          </div>
                        </div>

                        {/* Caption */}
                        <p className="text-gray-700 text-sm line-clamp-2 mb-4">
                          {post.caption}
                        </p>

                        {/* Stats */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1 text-gray-500">
                              <HiOutlineHeart className="w-4 h-4" />
                              <span className="text-xs font-medium">{post.likeCount || 0}</span>
                            </div>
                            <div className="flex items-center space-x-1 text-gray-500">
                              <HiOutlineChat className="w-4 h-4" />
                              <span className="text-xs font-medium">{post.commentCount || 0}</span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-end space-x-2 pt-3 border-t border-gray-100">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => openEditModal(post)}
                            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit post"
                          >
                            <HiOutlinePencil className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => openDeleteModal(post)}
                            className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete post"
                          >
                            <HiOutlineTrash className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Pagination */}
              {totalPages > 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8"
                >
                  <div className="text-sm text-gray-500">
                    Showing {((currentPage - 1) * postsPerPage) + 1} to{' '}
                    {Math.min(currentPage * postsPerPage, posts.length)} of {posts.length} posts
                  </div>

                  <div className="flex items-center space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg transition-colors ${
                        currentPage === 1
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      <HiOutlineChevronLeft className="w-5 h-5" />
                    </motion.button>
                    
                    <div className="flex items-center space-x-1">
                      {[...Array(totalPages)].map((_, i) => {
                        const pageNum = i + 1;
                        // Show current page, first, last, and adjacent pages
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <motion.button
                              key={pageNum}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-8 h-8 rounded-lg text-sm font-medium transition-all duration-300 ${
                                currentPage === pageNum
                                  ? "bg-gray-700 text-white shadow-md"
                                  : "text-gray-600 hover:bg-gray-100"
                              }`}
                            >
                              {pageNum}
                            </motion.button>
                          );
                        } else if (
                          pageNum === currentPage - 2 ||
                          pageNum === currentPage + 2
                        ) {
                          return (
                            <span key={pageNum} className="text-gray-400">...</span>
                          );
                        }
                        return null;
                      })}
                    </div>
                    
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-lg transition-colors ${
                        currentPage === totalPages
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      <HiOutlineChevronRight className="w-5 h-5" />
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Create Post Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => {
              setShowCreateModal(false);
              resetForm();
            }}
          >
            <motion.div
              ref={modalRef}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Create New Post</h2>
                    <p className="text-sm text-gray-500 mt-1">Share something with your audience</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <HiOutlineX className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Post Image <span className="text-red-500">*</span>
                    </label>
                    <div 
                      className={`border-2 border-dashed rounded-xl p-6 text-center hover:border-gray-400 transition-colors relative ${
                        formErrors.image ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file) {
                          const input = { target: { files: [file] } };
                          handleFileChange(input);
                        }
                      }}
                    >
                      {formData.imagePreview ? (
                        <div className="relative">
                          <img
                            src={formData.imagePreview}
                            alt="Preview"
                            className="max-h-64 mx-auto rounded-lg"
                          />
                          
                          {/* Upload progress */}
                          {imageUploadProgress > 0 && imageUploadProgress < 100 && (
                            <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                              <div className="bg-white rounded-lg p-4">
                                <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${imageUploadProgress}%` }}
                                    className="h-full bg-blue-500"
                                  />
                                </div>
                                <p className="text-sm text-white mt-2">Uploading... {imageUploadProgress}%</p>
                              </div>
                            </div>
                          )}
                          
                          <button
                            onClick={() => setFormData({ ...formData, image: null, imagePreview: "" })}
                            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <HiOutlineX className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <HiOutlineUpload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-sm text-gray-500 mb-2">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-400">PNG, JPG, GIF up to 5MB</p>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                        </>
                      )}
                    </div>
                    {formErrors.image && (
                      <p className="text-xs text-red-500 mt-1">{formErrors.image}</p>
                    )}
                  </div>

                  {/* Caption */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Caption <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="caption"
                      value={formData.caption}
                      onChange={handleInputChange}
                      rows="4"
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700 transition-colors ${
                        formErrors.caption ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Write your caption... (min. 10 characters)"
                    />
                    <div className="flex justify-between mt-1">
                      {formErrors.caption && (
                        <p className="text-xs text-red-500">{formErrors.caption}</p>
                      )}
                      <p className={`text-xs ${formData.caption.length > 400 ? 'text-orange-500' : 'text-gray-400'} ml-auto`}>
                        {formData.caption.length}/500
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setShowCreateModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreatePost}
                      disabled={submitting || !formData.caption.trim() || !formData.image}
                      className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Post'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Post Modal */}
      <AnimatePresence>
        {showEditModal && selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => {
              setShowEditModal(false);
              resetForm();
              setSelectedPost(null);
            }}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Edit Post</h2>
                    <p className="text-sm text-gray-500 mt-1">Update your post</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      resetForm();
                      setSelectedPost(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <HiOutlineX className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Post Image
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gray-400 transition-colors relative">
                      {formData.imagePreview ? (
                        <div className="relative">
                          <img
                            src={formData.imagePreview}
                            alt="Preview"
                            className="max-h-64 mx-auto rounded-lg"
                          />
                          <button
                            onClick={() => setFormData({ ...formData, image: null, imagePreview: "" })}
                            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <HiOutlineX className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <HiOutlineUpload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-sm text-gray-500 mb-2">
                            <span className="font-semibold">Click to upload</span> new image
                          </p>
                          <p className="text-xs text-gray-400">Leave empty to keep current image</p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Caption */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Caption <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="caption"
                      value={formData.caption}
                      onChange={handleInputChange}
                      rows="4"
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700 ${
                        formErrors.caption ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Write your caption..."
                    />
                    <div className="flex justify-between mt-1">
                      {formErrors.caption && (
                        <p className="text-xs text-red-500">{formErrors.caption}</p>
                      )}
                      <p className="text-xs text-gray-400 ml-auto">{formData.caption.length}/500</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setShowEditModal(false);
                        resetForm();
                        setSelectedPost(null);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEditPost}
                      disabled={submitting}
                      className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Updating...
                        </>
                      ) : (
                        'Update Post'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Post Modal */}
      <AnimatePresence>
        {showViewModal && selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => {
              setShowViewModal(false);
              setSelectedPost(null);
            }}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-800">Post Details</h2>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedPost(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <HiOutlineX className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* College Info */}
                  {selectedPost.createdBy?.collegeName && (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center space-x-3 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200"
                    >
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <HiOutlineOfficeBuilding className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Posted by</p>
                        <p className="text-lg font-semibold text-gray-800">
                          {selectedPost.createdBy.collegeName}
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* Image */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 }}
                    className="relative rounded-xl overflow-hidden border border-gray-200"
                  >
                    <img
                      src={selectedPost.image}
                      alt={selectedPost.caption}
                      className="w-full max-h-[400px] object-contain bg-gray-50"
                    />
                  </motion.div>

                  {/* Caption */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="p-4 bg-gray-50 rounded-xl border border-gray-200"
                  >
                    <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                      {selectedPost.caption}
                    </p>
                  </motion.div>

                  {/* Stats */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="flex items-center space-x-6 p-4 bg-gray-50 rounded-xl border border-gray-200"
                  >
                    <div className="flex items-center space-x-2">
                      <HiOutlineHeart className="w-5 h-5 text-gray-500" />
                      <span className="text-gray-700 font-medium">{selectedPost.likeCount || 0} likes</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <HiOutlineChat className="w-5 h-5 text-gray-500" />
                      <span className="text-gray-700 font-medium">{selectedPost.commentCount || 0} comments</span>
                    </div>
                  </motion.div>

                  {/* Comments Section */}
                  {selectedPost.comments && selectedPost.comments.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="space-y-4"
                    >
                      <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                        <HiOutlineChat className="w-5 h-5" />
                        Comments ({selectedPost.comments.length})
                      </h3>
                      <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                        {selectedPost.comments.map((comment, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * index }}
                            className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-center space-x-2 mb-1">
                              <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                                <span className="text-xs font-medium text-gray-600">
                                  {comment.user?.fullName?.charAt(0) || 'U'}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-gray-700">
                                {comment.user?.fullName || 'User'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 ml-8">{comment.text}</p>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedPost(null);
            }}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-2xl shadow-xl w-full max-w-md border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-2 text-red-600">
                    <HiOutlineExclamation className="w-6 h-6" />
                    <h3 className="text-lg font-bold">Confirm Deletion</h3>
                  </div>
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedPost(null);
                    }}
                    className="p-1 hover:bg-gray-100 rounded-lg"
                  >
                    <HiOutlineX className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    Are you sure you want to delete this post? This action cannot be undone.
                  </p>
                  {selectedPost.image && (
                    <div className="flex justify-center">
                      <img
                        src={selectedPost.image}
                        alt="Preview"
                        className="h-32 object-contain rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedPost(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeletePost}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Delete Post'
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Posts;